import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, FileQuestion, Lock } from 'lucide-react';
import { LessonLearningLayout } from '@/components/features/learning/navigation/LessonLearningLayout';
import { LessonBootLoading } from '@/components/features/learning/lesson/LessonBootLoading';
import { ApiError, lessonApi } from '@/lib/api-client';
import { track } from '@/config/observability';
import { queryKeys } from '@/config/query-client';
import { usePipelineStatus } from '@/hooks/lesson/usePipelineStatus';
import { useRefreshSourcesWhenRead } from '@/hooks/lesson/useSources';
import { ErrorState } from '@/components/core/common/boundaries/ErrorState';

/**
 * The lesson page: mounts the lesson layout as soon as there is anything to
 * show, and lets each panel say what is still being made.
 *
 *   1. `LessonBootLoading` shows only until the first pipeline poll (and the
 *      lesson metadata) answers - "Creating your lesson" for a lesson still
 *      in flight, "Loading lesson" for one that is already built.
 *   2. Then `LessonLearningLayout` mounts, even while the overview (title +
 *      summary) is still being written: the centre's build view
 *      (`LessonBuildProgress`) narrates the build, the top bar shows the
 *      user's own prompt as a provisional title, and the Prompt panel shows
 *      the prompt. When the overview completes, metadata is refetched so the
 *      generated title swaps in.
 *   3. No failure is whole-page any more. A failed overview is a line in the
 *      build view plus a notice with Retry in the Prompt panel (the lesson
 *      keeps building without it); a failed source ingest or context build is
 *      handled in the build view; content failures stay inside their panels.
 *
 * The only full-page states left are 404 / 403 and "couldn't load" (no
 * snapshot at all and a query has errored).
 */
export function LessonLearningPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Lesson metadata - title, description, tags, the user's prompt, error.
  // Not polled: refetched when the overview completes (the generated title)
  // and when the pipeline turns terminal (final status + error).
  const {
    data: lessonData,
    refetch: refetchLesson,
    error: lessonError,
  } = useQuery({
    queryKey: queryKeys.lessons.metadata(id || ''),
    // Don't normalize `staged_source_ids: []` here - the backend now
    // initializes `staged_source_ids` to the current version's source_ids
    // on every version creation (initial v1 + create_new_version), so an
    // explicit `[]` always means "the user unchecked everything." Treating
    // it as "no staged state" would silently revert that intent on every
    // 3-second poll and break the duplicate-version detection.
    queryFn: () => lessonApi.getLesson(id!),
    // No refetchInterval: `pipeline_status` drives per-step UI from its own
    // poll. Metadata is refetched when the overview completes and when the
    // lesson reaches a terminal state (both handled below via `useEffect`).
    // Cache for 5 min so revisits stay fast.
    staleTime: 5 * 60 * 1000,
    // Fail fast on 404/403 so the "Lesson not found" / "No access"
    // branch renders immediately instead of stalling behind exponential-
    // backoff retries (which would take ~7s for a hard 404). Other
    // statuses keep the usual 3-retry transient-error policy.
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      if (status === 404 || status === 403) return false;
      return failureCount < 3;
    },
    enabled: !!id,
  });

  // One `lesson_opened` per lesson visited, once its metadata has loaded.
  const openedLessonRef = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !lessonData || openedLessonRef.current === id) return;
    openedLessonRef.current = id;
    track('lesson_opened', {
      lesson_id: id,
      overall_status: lessonData.status,
    });
  }, [id, lessonData]);

  // Aggregated per-step status. One poll, drives every panel and the
  // page-level `lessonDone` derivation below.
  const pipeline = usePipelineStatus(id, { intervalMs: 3000 });
  useRefreshSourcesWhenRead(id, pipeline.data?.sources?.overall);

  // When the pipeline flips to a terminal state, refetch metadata once
  // to pick up the final `status` + `error` (the OVERVIEW_GENERATION_FAILED
  // catastrophic-failure code lives on metadata, not pipeline-status).
  // This replaces what used to be a 3 s metadata poll.
  const pipelineOverallStatus = pipeline.data?.overall_status;
  useEffect(() => {
    if (
      pipelineOverallStatus === 'completed' ||
      pipelineOverallStatus === 'failed'
    ) {
      refetchLesson();
    }
  }, [pipelineOverallStatus, refetchLesson]);

  // The overview writes the lesson's real title + summary. The layout is up
  // long before it finishes (showing the typed prompt as the title), so pick
  // up the generated title the moment the step completes.
  // Only on the transition: a revisited lesson is already completed on the
  // first poll and its metadata is fresh.
  const overviewStepState = pipeline.data?.steps?.overview?.state;
  const prevOverviewState = useRef(overviewStepState);
  useEffect(() => {
    const prev = prevOverviewState.current;
    prevOverviewState.current = overviewStepState;
    if (
      overviewStepState === 'completed' &&
      prev !== undefined &&
      prev !== 'completed'
    ) {
      refetchLesson();
    }
  }, [overviewStepState, refetchLesson]);

  // Single source of truth for "the page has enough information to
  // make a meaningful state claim per tab." True only when BOTH the
  // lesson metadata and the pipeline-status snapshot have landed.
  // Until then every <StepGate> downstream falls back to a neutral
  // "Loading X" skeleton instead of flashing "Preparing X" / "Waiting
  // - X" / a premature Generate CTA based on default values.
  const lessonReady = lessonData !== undefined && pipeline.data !== undefined;

  if (!id) {
    navigate('/my-learning');
    return null;
  }

  const handleGoBack = () => navigate('/my-learning');

  // Lesson doesn't exist / user can't see it. Without this branch the
  // query just leaves `lessonData` undefined forever (404s skip retry
  // per `query-client.ts`), and the page would spin indefinitely.
  if (lessonError instanceof ApiError) {
    if (lessonError.status === 404) {
      return (
        <ErrorState
          variant='page'
          icon={FileQuestion}
          title='Lesson not found'
          description="This lesson doesn't exist, or it may have been deleted."
          primaryAction={{
            label: 'Back to My Learning',
            onClick: handleGoBack,
          }}
        />
      );
    }
    if (lessonError.status === 403) {
      return (
        <ErrorState
          variant='page'
          icon={Lock}
          title="You don't have access to this lesson"
          description='Ask the owner to share it with you, or pick another lesson from My Learning.'
          primaryAction={{
            label: 'Back to My Learning',
            onClick: handleGoBack,
          }}
        />
      );
    }
  }

  // Boot gate: hold the neutral loader only until the first pipeline poll has
  // answered AND the metadata has either landed or given up. The metadata
  // carries `user_prompt` (the provisional title + the Prompt panel) and
  // `has_user_sources` (the build view's wording). Two escapes keep a
  // one-shot metadata query from pinning the loader: a lesson whose overview
  // is already completed mounts without it (panels show fetch skeletons
  // until it lands - the pipeline poll self-heals, the metadata query does
  // not), and an errored metadata query mounts the layout anyway.
  const overviewState = pipeline.data?.steps?.overview?.state;
  const booted =
    pipeline.data !== undefined &&
    (lessonData !== undefined ||
      lessonError != null ||
      overviewState === 'completed');
  if (!booted) {
    // First load failed (after retries) → surface a real error with a
    // Try-again action instead of an endless spinner. The pipeline poll keeps
    // retrying on its own, so a transient blip (e.g. the backend restarting)
    // self-heals; this only shows when we genuinely have no snapshot AND a
    // query has errored. 404/403 are handled above.
    if (!pipeline.data && (pipeline.error != null || lessonError != null)) {
      return (
        <ErrorState
          variant='page'
          icon={AlertCircle}
          title="Couldn't load this lesson"
          description="We couldn't reach the server. Check your connection, then try again."
          primaryAction={{
            label: 'Try again',
            onClick: () => {
              refetchLesson();
              pipeline.refetch();
            },
          }}
          secondaryAction={{
            label: 'Back to My Learning',
            onClick: handleGoBack,
          }}
        />
      );
    }
    // "Creating" only when the metadata says the lesson is in flight; an
    // already-built lesson (or metadata not here yet) reads "Loading".
    const creating =
      lessonData?.status === 'pending' || lessonData?.status === 'processing';
    return <LessonBootLoading creating={creating} />;
  }

  // Layout is mounted. Each panel reads from
  // `pipelineStatus` and decides:
  //   * `undefined` (this prop)        → initial-load skeleton (A1/A2)
  //   * step not in map                 → off-flow placeholder (B1)
  //   * step pending/processing         → skeleton with status pill (B3/B4)
  //   * step completed                  → content (B5)
  //   * step failed                     → failure card with error info (B6)
  //
  // Falling back to `|| {}` would silently flip "loading" into "off-flow"
  // for every step and lock chat permanently if the first poll failed -
  // keep the undefined signal intact.
  // Derived from pipeline-status - it polls every 3s and is the
  // freshest signal we have. Metadata reflects the same terminal flip
  // but lags now that we don't poll it. Falls back to metadata's
  // `status` only if pipeline-status hasn't arrived yet on first
  // mount (rare gap).
  //
  // A real fallback, not an OR. The two disagree during a re-version: v2's
  // overview task writes `status: completed` to the lesson row as soon as the
  // overview is done (`overview_task.py`), while `byte_selection` is still
  // building. OR-ing them called that lesson done - the chat then showed
  // "didn't finish setting up" and StepGate failure cards mid-build.
  const lessonDone =
    pipelineOverallStatus !== undefined
      ? pipelineOverallStatus === 'completed' ||
        pipelineOverallStatus === 'failed'
      : lessonData?.status === 'completed' || lessonData?.status === 'failed';

  return (
    <LessonLearningLayout
      lessonId={id}
      lessonMetadata={lessonData}
      steps={pipeline.data?.steps}
      sources={pipeline.data?.sources}
      lessonDone={lessonDone}
      lessonReady={lessonReady}
      onRetry={() => {
        refetchLesson();
        pipeline.refetch();
      }}
    />
  );
}
